import { useEffect, useId, useRef, useState } from "react";
import { Calendar } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import * as s from "./CleanDatePicker.css";

// ---------------------------------------------------------------------------
// <CleanDatePicker> — drop-in replacement for `@progress/kendo-react-dateinputs`
// `DatePicker` that fixes the masked-input character-swallowing bug.
//
// Background (audit/25-parity-roundtrip-2.md §D-3): Kendo's masked DateInput
// interprets typed digits section-by-section and auto-completes mid-stream.
// `pressSequentially("05/28/2026")` ended up as `5/24/2026` in state because
// the mask consumed digits, advanced the section, and the rest of the digits
// landed in the wrong sections.
//
// This component sidesteps the mask entirely: a plain `<input type="text">`
// accepts free-form typed text (MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY all work)
// and only commits to state on blur, after a deterministic parse. A small
// calendar button toggles a Kendo Calendar popup for users who prefer
// clicking — the popup is the same component the legacy Kendo DatePicker
// uses, so the calendar UX is unchanged.
//
// Public API mirrors the subset of `DatePickerProps` we actually use:
//   value, onChange (with `e.value: Date | null`), disabled, format,
//   formatPlaceholder, width.
// ---------------------------------------------------------------------------

export interface CleanDatePickerChangeEvent {
  /** Mirrors Kendo's `DatePickerChangeEvent.value` shape. */
  value: Date | null;
  /** The originating event, if any. Optional/best-effort. */
  nativeEvent?: Event;
}

export interface CleanDatePickerProps {
  value?: Date | null;
  onChange?: (e: CleanDatePickerChangeEvent) => void;
  disabled?: boolean;
  width?: number | string;
  /** Display format, MM/dd/yyyy by default. */
  format?: string;
  /** Placeholder string shown when the input is empty. */
  placeholder?: string;
  /** Optional id for label-for association. */
  id?: string;
  /** Forwarded to the underlying `<input>`. */
  name?: string;
  /** Forwarded as `aria-label` on the underlying `<input>`. */
  ariaLabel?: string;
  /** Min selectable date — passed through to the Calendar popup. */
  min?: Date;
  /** Max selectable date — passed through to the Calendar popup. */
  max?: Date;
  className?: string;
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

/**
 * Format a Date as `MM/DD/YYYY`. Returns empty string for null / invalid
 * input so we never render "NaN/NaN/NaN" or similar in the box.
 */
const formatDate = (d: Date | null | undefined): string => {
  if (!d) return "";
  const t = d.getTime();
  if (Number.isNaN(t)) return "";
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
};

/**
 * Parse a date from free-form text. Accepts:
 *   - MM/DD/YYYY, M/D/YYYY (US-style — primary)
 *   - MM-DD-YYYY, M-D-YYYY (dash-separated)
 *   - YYYY-MM-DD (ISO date, no time)
 *   - Anything `new Date(str)` can handle, as a last resort
 * Returns `null` if the parse fails OR if the resulting date is implausible
 * (year < 1900 / > 2999 — protects against `0/0/0001` from a half-typed
 * value).
 */
const parseDate = (raw: string): Date | null => {
  const text = raw.trim();
  if (!text) return null;

  // ISO first — least ambiguous.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) {
    const [, y, m, d] = iso;
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return validate(dt, Number(y), Number(m), Number(d));
  }

  // US-style MM/DD/YYYY or MM-DD-YYYY.
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/.exec(text);
  if (us) {
    const [, m, d, yRaw] = us;
    let year = Number(yRaw);
    // Two-digit year: 00-69 → 2000s, 70-99 → 1900s. Matches Excel/Outlook.
    if (yRaw.length === 2) year = year >= 70 ? 1900 + year : 2000 + year;
    const dt = new Date(year, Number(m) - 1, Number(d));
    return validate(dt, year, Number(m), Number(d));
  }

  // Last resort — Date.parse for anything else (e.g. "May 28 2026").
  const fallback = new Date(text);
  if (Number.isNaN(fallback.getTime())) return null;
  if (fallback.getFullYear() < 1900 || fallback.getFullYear() > 2999) {
    return null;
  }
  return fallback;
};

/**
 * Sanity-check a constructed Date — JS Date constructor silently overflows
 * (e.g. month=13 → next year January). Reject any result whose Y/M/D
 * doesn't match what we asked for.
 */
const validate = (
  dt: Date,
  year: number,
  month: number,
  day: number
): Date | null => {
  if (year < 1900 || year > 2999) return null;
  if (
    dt.getFullYear() !== year ||
    dt.getMonth() !== month - 1 ||
    dt.getDate() !== day
  ) {
    return null;
  }
  return dt;
};

const CleanDatePicker = ({
  value,
  onChange,
  disabled,
  width,
  placeholder = "MM/DD/YYYY",
  id: idProp,
  name,
  ariaLabel,
  min,
  max,
  className,
}: CleanDatePickerProps) => {
  // Stable id for label-association if the caller didn't supply one.
  const autoId = useId();
  const inputId = idProp ?? autoId;

  // Local string state for the input — only commits to `onChange` on blur or
  // calendar pick. Keeps typing fluid and avoids partial-parse flapping.
  const [text, setText] = useState<string>(formatDate(value ?? null));
  const [popupOpen, setPopupOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Sync local text when the controlled `value` changes upstream (e.g.
  // form reset or a sibling DatePicker driving this one).
  useEffect(() => {
    // Only overwrite the text if the upstream value disagrees with what
    // the user currently has parsed — otherwise we'd clobber in-progress
    // typing on every render.
    const current = parseDate(text);
    const same =
      (current == null && value == null) ||
      (current != null &&
        value != null &&
        current.getTime() === value.getTime());
    if (!same) {
      setText(formatDate(value ?? null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close the popup on outside click. The Popup component itself doesn't
  // self-close — we have to listen for clicks outside both the input row
  // and the popup body.
  useEffect(() => {
    if (!popupOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setPopupOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [popupOpen]);

  const commitText = (raw: string) => {
    const parsed = parseDate(raw);
    // Normalise the displayed text to canonical formatting on commit, so
    // `5/8/2026` becomes `05/08/2026` after blur. If parsing failed AND
    // the user typed nothing, we still emit `null` to the caller; if they
    // typed garbage, we restore the previous valid value to avoid stranding
    // an unparseable string in the box.
    if (parsed) {
      setText(formatDate(parsed));
      onChange?.({ value: parsed });
    } else if (raw.trim() === "") {
      setText("");
      onChange?.({ value: null });
    } else {
      // Garbage in — restore to last known good value.
      setText(formatDate(value ?? null));
    }
  };

  const onCalendarChange = (e: { value: Date | null }) => {
    const next = e.value ?? null;
    setText(formatDate(next));
    onChange?.({ value: next });
    setPopupOpen(false);
  };

  return (
    <div
      ref={anchorRef}
      className={`${s.wrap}${className ? ` ${className}` : ""}`}
      style={width != null ? { width } : undefined}
    >
      <input
        id={inputId}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={s.input}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitText((e.target as HTMLInputElement).value);
          } else if (e.key === "Escape") {
            setText(formatDate(value ?? null));
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <button
        type="button"
        className={s.button}
        disabled={disabled}
        aria-label="Open calendar"
        tabIndex={-1}
        onClick={() => setPopupOpen((p) => !p)}
      >
        {/* Tiny inline calendar glyph — keeps us out of icon-font territory. */}
        <span aria-hidden="true">📅</span>
      </button>
      <Popup
        anchor={anchorRef.current}
        show={popupOpen}
        popupClass={s.popup}
        animate={false}
      >
        <div ref={popupRef}>
          <Calendar
            value={value ?? null}
            onChange={onCalendarChange}
            min={min}
            max={max}
          />
        </div>
      </Popup>
    </div>
  );
};

export default CleanDatePicker;
